import type { ParameterOption } from '@shared/types';

export type StepperParseResult = {
  type: 'stepper';
  value: string | number;
  options: ParameterOption[];
  stepperLoop?: boolean;
  /** Remaining comment after stripping the <...> block */
  comment: string;
};

/**
 * Detects stepper syntax in the comment: leading `<...>` block.
 *
 * Syntax: `<val1, val2>`  or  `<0:Off, 1:On>`  or  `<loop: 0:A, 1:B>`
 *
 * This is checked before value-type branching so it works for both
 * number and string default values.
 *
 * Returns null if the comment does not start with a `<...>` stepper block.
 */
export function parseStepperType(
  valueStr: string,
  comment: string,
): StepperParseResult | null {
  const stepperMatch = comment.match(/^<([^>]*)>(.*)/s);
  if (!stepperMatch) return null;

  let stepperContent = stepperMatch[1].trim();
  const remainingComment = stepperMatch[2].trim();

  let stepperLoop: boolean | undefined = undefined;
  if (/^loop\s*:/i.test(stepperContent)) {
    stepperLoop = true;
    stepperContent = stepperContent.replace(/^loop\s*:\s*/i, '').trim();
  }

  const stepperTokens = stepperContent
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');

  const options: ParameterOption[] = stepperTokens.map((tok) => {
    const colonIdx = tok.indexOf(':');
    if (colonIdx !== -1) {
      const rawVal = tok.slice(0, colonIdx).trim();
      const label = tok.slice(colonIdx + 1).trim();
      const num = Number(rawVal);
      return { value: isNaN(num) ? rawVal : num, label };
    }
    // Plain token — value and label are the same
    const num = Number(tok);
    const val = isNaN(num) ? tok.replace(/^"|"$/g, '') : num;
    return { value: val, label: String(val) };
  });

  if (options.length === 0) return null;

  // Determine the actual value from valueStr
  const numVal = Number(valueStr);
  let value: string | number;
  if (!isNaN(numVal)) {
    value = numVal;
  } else if (valueStr.startsWith('"') && valueStr.endsWith('"')) {
    value = valueStr.slice(1, -1);
  } else {
    value = valueStr;
  }

  return {
    type: 'stepper',
    value,
    options,
    stepperLoop,
    comment: remainingComment,
  };
}
